import { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchItem {
  value: string;
  /** Text shown in the list row. */
  label: string;
  /** Lowercased haystack matched against the query. */
  keywords: string;
}

/**
 * A lightweight searchable dropdown (combobox): a trigger button that opens a
 * filterable list. Used for the phone country-code and address country pickers.
 */
export function SearchSelect({
  value,
  items,
  trigger,
  onChange,
  placeholder = 'Search…',
  minWidth = 260,
}: {
  value: string;
  items: SearchItem[];
  /** What the closed trigger button shows for the current value. */
  trigger: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ('');
      inputRef.current?.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((i) => i.keywords.includes(needle));
  }, [items, q]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="input rdr-cc-trigger"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}
      >
        {trigger}
        <span aria-hidden style={{ marginLeft: 'auto', color: 'var(--text-3)' }}>
          ▾
        </span>
      </button>
      {open && (
        <div
          className="card rdr-cc-pop"
          style={{
            position: 'absolute',
            zIndex: 50,
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth,
            maxWidth: 360,
            maxHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'var(--sh-md, 0 8px 24px rgba(0,0,0,.15))',
            background: 'var(--bg-surface, #fff)',
            border: '1px solid var(--border, #ddd)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <input
            ref={inputRef}
            className="input"
            placeholder={placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ margin: 8, width: 'auto' }}
          />
          <div style={{ overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 12px', color: 'var(--text-3)', fontSize: 13 }}>
                No matches
              </div>
            ) : (
              filtered.map((i) => (
                <button
                  key={i.value}
                  type="button"
                  onClick={() => {
                    onChange(i.value);
                    setOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    border: 'none',
                    background: i.value === value ? 'var(--accent-soft, #eef)' : 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: 13.5,
                  }}
                  onMouseEnter={(e) => {
                    if (i.value !== value) e.currentTarget.style.background = 'var(--bg-sunken, #f5f5f5)';
                  }}
                  onMouseLeave={(e) => {
                    if (i.value !== value) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {i.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
